import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

import type { DocumentExtractionResponse, LocationStepData } from '@tankef/core';
import { logger } from '@tankef/env';

import { withDevtools } from './middleware-utils';

/**
 * Unified session management supporting both:
 * - Pre-KYC: Registration (email, phone, location, password, identity)
 * - Post-KYC: Onboarding (personal data, documents, profile, beneficiary, contract)
 *
 * Replaces useRegistrationStore and useOnboardingSessionStore with single source of truth.
 */

export interface ValidatedField {
  value: string;
  verifiedAt: number;
  method?: 'sms' | 'whatsapp' | 'email';
}

export type SessionPhase = 'registration' | 'onboarding';

export type RegistrationStep =
  | 'location'
  | 'email'
  | 'password'
  | 'phone'
  | 'identity'
  | 'complete';
export type OnboardingStep =
  | 'data'
  | 'address'
  | 'curp'
  | 'rfc'
  | 'transactional_profile'
  | 'profile'
  | 'beneficiary'
  | 'contract'
  | 'complete';

export type SessionStep = RegistrationStep | OnboardingStep;

export interface UnifiedSessionData {
  // Identity & Timing
  sessionId: string;
  phase: SessionPhase;
  createdAt: number;
  expiresAt: number;

  // Registration Phase Data (Pre-KYC)
  registration?: {
    location?: LocationStepData;
    email?: string;
    pendingEmail?: string; // Accepted by the BE and awaiting OTP; `email` is only set once verified
    emailVerified?: boolean;
    phone?: string;
    phoneVerified?: boolean;
    phoneMethod?: 'sms' | 'whatsapp';
    password?: string; // Never persisted, only in-memory
    passwordSet?: boolean;
    identityVerified?: boolean;
    onboardingToken?: string; // Pre-auth token
  };

  // Onboarding Phase Data (Post-KYC)
  onboarding?: {
    documentExtraction?: DocumentExtractionResponse;
    /**
     * Snapshot of the `data` step form (`PersonFormData`) once the user
     * submits it. Fallback source of truth for `confirm-data` when JAAK
     * extracted nothing (`documentExtraction` stays undefined) — without
     * persisting this, a reload mid-flow wipes it (it used to live only in
     * component state) and the user could never advance past `curp`.
     */
    personData?: Record<string, unknown>;
    /** Same rationale as `personData`, for the `address` step (`AddressFormData`). */
    editedAddress?: Record<string, unknown>;
    signingSessionId?: string;
    currentStep?: OnboardingStep;
  };

  // Validation Cache (shared across both phases)
  validatedData: {
    email?: ValidatedField;
    phone?: ValidatedField;
    location?: LocationStepData;
    personalData?: Record<string, unknown>;
    curpBlacklist?: { curp: string; validatedAt: number };
    [key: string]:
      | ValidatedField
      | Record<string, unknown>
      | { curp: string; validatedAt: number }
      | LocationStepData
      | undefined;
  };

  // Current Progress
  currentStep: SessionStep;

  // Recovery & Error Tracking
  isRecovered: boolean;
  recoveredFrom?: SessionStep;
  recoveryAttempt?: number;

  // Pending Operations
  pendingOperations: Array<{
    field: string;
    status: 'pending' | 'failed';
    error?: string;
    timestamp: number;
  }>;
}

export interface UnifiedSessionState {
  session: UnifiedSessionData | null;
  isLoading: boolean;
  error: Error | null;

  // Initialization & Lifecycle
  initRegistrationSession: (sessionId?: string) => Promise<void>;
  initOnboardingSession: (sessionId?: string) => Promise<void>;
  verifySession: () => Promise<boolean>;
  advancePhase: (fromRegistration: boolean) => void; // true = registration→onboarding

  // Step Management
  setCurrentStep: (step: SessionStep) => void;
  goToStep: (step: SessionStep) => void; // Alias for setCurrentStep (for compatibility)
  goToNextStep: () => void;
  goToPrevStep: () => void;
  nextStep: () => void; // Alias for goToNextStep (backward compatibility)
  prevStep: () => void; // Alias for goToPrevStep (backward compatibility)

  // Data Management
  setRegistrationData: (data: Partial<UnifiedSessionData['registration']>) => void;
  setOnboardingData: (data: Partial<UnifiedSessionData['onboarding']>) => void;

  // Convenience setters for registration (for backward compatibility during migration)
  setLocation: (location: LocationStepData) => void;
  setEmail: (email: string) => void;
  setPassword: (password: string) => void;
  setPhone: (phone: string) => void;
  setPhoneVerified: (verified: boolean) => void;
  setIdentityVerified: (verified: boolean) => void;
  setOnboardingToken: (token: string) => void;

  // Convenience setters for onboarding
  setDocumentExtraction: (data: DocumentExtractionResponse) => void;
  setSigningSessionId: (id: string) => void;

  // Validation Caching
  markValidated: (field: string, value: ValidatedField | Record<string, unknown>) => void;
  isFieldValidated: (field: string, ageThresholdMs?: number) => boolean;
  getValidatedField: (field: string) => ValidatedField | Record<string, unknown> | undefined;

  // Recovery
  recover: (fromStep: SessionStep) => void;

  // Pending Operations
  setPendingOperation: (field: string, status: 'pending' | 'failed', error?: string) => void;
  clearPendingOperation: (field: string) => void;

  // Reset
  reset: () => void;
  clearError: () => void;
}

const VALIDATION_CACHE_DURATION = 24 * 60 * 60 * 1000; // 24 hours
const SESSION_EXPIRY_BUFFER = 5 * 60 * 1000; // 5 minutes

const REGISTRATION_STEPS: RegistrationStep[] = [
  'location',
  'email',
  'password',
  'phone',
  'identity',
  'complete',
];
const ONBOARDING_STEPS: OnboardingStep[] = [
  'data',
  'address',
  'curp',
  'rfc',
  'transactional_profile',
  'profile',
  'beneficiary',
  'contract',
  'complete',
];

const getNextStep = (current: SessionStep, phase: SessionPhase): SessionStep => {
  if (phase === 'registration') {
    const index = REGISTRATION_STEPS.indexOf(current as RegistrationStep);
    const nextIndex = Math.min(index + 1, REGISTRATION_STEPS.length - 1);
    return (REGISTRATION_STEPS[nextIndex] ?? 'complete') as SessionStep;
  } else {
    const index = ONBOARDING_STEPS.indexOf(current as OnboardingStep);
    const nextIndex = Math.min(index + 1, ONBOARDING_STEPS.length - 1);
    return (ONBOARDING_STEPS[nextIndex] ?? 'complete') as SessionStep;
  }
};

const getPrevStep = (current: SessionStep, phase: SessionPhase): SessionStep => {
  if (phase === 'registration') {
    const index = REGISTRATION_STEPS.indexOf(current as RegistrationStep);
    const prevIndex = Math.max(index - 1, 0);
    return (REGISTRATION_STEPS[prevIndex] ?? REGISTRATION_STEPS[0]) as SessionStep;
  } else {
    const index = ONBOARDING_STEPS.indexOf(current as OnboardingStep);
    const prevIndex = Math.max(index - 1, 0);
    return (ONBOARDING_STEPS[prevIndex] ?? ONBOARDING_STEPS[0]) as SessionStep;
  }
};

export const useUnifiedSessionStore = create<UnifiedSessionState>()(
  withDevtools(
    'UnifiedSession',
    persist(
      (set, get) => ({
        session: null,
        isLoading: false,
        error: null,

        // Initialize registration session
        initRegistrationSession: async (sessionId?: string) => {
          set({ isLoading: true, error: null });
          try {
            const existing = get().session;

            if (sessionId) {
              logger.info('[UnifiedSession] Loading registration session from ID:', { sessionId });
              set(state => {
                if (state.session) return state;
                return {
                  ...state,
                  session: {
                    sessionId,
                    phase: 'registration' as SessionPhase,
                    createdAt: Date.now(),
                    expiresAt: Date.now() + 24 * 60 * 60 * 1000,
                    validatedData: {},
                    currentStep: 'location' as SessionStep,
                    isRecovered: true,
                    pendingOperations: [],
                  },
                };
              });
              return;
            }

            if (
              existing &&
              existing.phase === 'registration' &&
              existing.expiresAt > Date.now() + SESSION_EXPIRY_BUFFER
            ) {
              logger.info('[UnifiedSession] Resuming existing registration session');
              return;
            }

            logger.info('[UnifiedSession] Creating new registration session');
            set({ session: null });
          } catch (err) {
            const error =
              err instanceof Error ? err : new Error('Failed to initialize registration session');
            logger.error('[UnifiedSession] Init failed:', error);
            set({ error });
            throw error;
          } finally {
            set({ isLoading: false });
          }
        },

        // Initialize onboarding session
        initOnboardingSession: async (sessionId?: string) => {
          set({ isLoading: true, error: null });
          try {
            const existing = get().session;

            if (sessionId) {
              logger.info('[UnifiedSession] Loading onboarding session from ID:', { sessionId });
              set(state => {
                if (state.session) return state;
                return {
                  ...state,
                  session: {
                    sessionId,
                    phase: 'onboarding' as SessionPhase,
                    createdAt: Date.now(),
                    expiresAt: Date.now() + 24 * 60 * 60 * 1000,
                    validatedData: {},
                    currentStep: 'data' as SessionStep,
                    isRecovered: true,
                    pendingOperations: [],
                  },
                };
              });
              return;
            }

            if (
              existing &&
              existing.phase === 'onboarding' &&
              existing.expiresAt > Date.now() + SESSION_EXPIRY_BUFFER
            ) {
              logger.info('[UnifiedSession] Resuming existing onboarding session');
              return;
            }

            logger.info('[UnifiedSession] Creating new onboarding session');
            set({ session: null });
          } catch (err) {
            const error =
              err instanceof Error ? err : new Error('Failed to initialize onboarding session');
            logger.error('[UnifiedSession] Onboarding init failed:', error);
            set({ error });
            throw error;
          } finally {
            set({ isLoading: false });
          }
        },

        // Verify session is still valid
        verifySession: async () => {
          const session = get().session;
          if (!session) return false;

          const isExpired = session.expiresAt < Date.now();
          if (isExpired) {
            logger.warn('[UnifiedSession] Session expired');
            set({ session: null });
            return false;
          }

          return true;
        },

        // Transition from registration to onboarding
        advancePhase: (fromRegistration: boolean) => {
          set(state => {
            if (!state.session || (fromRegistration && state.session.phase !== 'registration'))
              return state;

            logger.info('[UnifiedSession] Advancing phase: registration → onboarding');

            return {
              ...state,
              session: {
                ...state.session,
                phase: 'onboarding' as SessionPhase,
                currentStep: 'data' as SessionStep,
                onboarding: {
                  currentStep: 'data' as OnboardingStep,
                },
              },
            };
          });
        },

        // Step management
        setCurrentStep: (step: SessionStep) => {
          set(state => {
            if (!state.session) return state;
            return { ...state, session: { ...state.session, currentStep: step } };
          });
        },

        goToStep: (step: SessionStep) => {
          set(state => {
            if (!state.session) return state;
            return { ...state, session: { ...state.session, currentStep: step } };
          });
        },

        goToNextStep: () => {
          set(state => {
            if (!state.session) return state;
            const next = getNextStep(state.session.currentStep, state.session.phase);
            return { ...state, session: { ...state.session, currentStep: next } };
          });
        },

        goToPrevStep: () => {
          set(state => {
            if (!state.session) return state;
            const prev = getPrevStep(state.session.currentStep, state.session.phase);
            return { ...state, session: { ...state.session, currentStep: prev } };
          });
        },

        // Aliases for backward compatibility
        nextStep: () => {
          get().goToNextStep();
        },

        prevStep: () => {
          get().goToPrevStep();
        },

        // Data setters
        setRegistrationData: (data: Partial<UnifiedSessionData['registration']>) => {
          set(state => {
            if (!state.session || state.session.phase !== 'registration') return state;
            return {
              ...state,
              session: {
                ...state.session,
                registration: { ...state.session.registration, ...data },
              },
            };
          });
        },

        setOnboardingData: (data: Partial<UnifiedSessionData['onboarding']>) => {
          set(state => {
            if (!state.session || state.session.phase !== 'onboarding') return state;
            return {
              ...state,
              session: {
                ...state.session,
                onboarding: { ...state.session.onboarding, ...data },
              },
            };
          });
        },

        // Convenience setters for registration (for backward compatibility)
        setLocation: (location: LocationStepData) => {
          set(state => {
            if (!state.session || state.session.phase !== 'registration') return state;
            return {
              ...state,
              session: {
                ...state.session,
                registration: { ...state.session.registration, location },
              },
            };
          });
        },

        setEmail: (email: string) => {
          set(state => {
            if (!state.session || state.session.phase !== 'registration') return state;
            return {
              ...state,
              session: {
                ...state.session,
                registration: { ...state.session.registration, email },
              },
            };
          });
        },

        setPassword: (password: string) => {
          set(state => {
            if (!state.session || state.session.phase !== 'registration') return state;
            return {
              ...state,
              session: {
                ...state.session,
                registration: {
                  ...state.session.registration,
                  password,
                  passwordSet: true,
                },
              },
            };
          });
        },

        setPhone: (phone: string) => {
          set(state => {
            if (!state.session || state.session.phase !== 'registration') return state;
            return {
              ...state,
              session: {
                ...state.session,
                registration: {
                  ...state.session.registration,
                  phone,
                  phoneVerified: false,
                },
              },
            };
          });
        },

        setPhoneVerified: (phoneVerified: boolean) => {
          set(state => {
            if (!state.session || state.session.phase !== 'registration') return state;
            return {
              ...state,
              session: {
                ...state.session,
                registration: { ...state.session.registration, phoneVerified },
              },
            };
          });
        },

        setIdentityVerified: (identityVerified: boolean) => {
          set(state => {
            if (!state.session || state.session.phase !== 'registration') return state;
            return {
              ...state,
              session: {
                ...state.session,
                registration: { ...state.session.registration, identityVerified },
              },
            };
          });
        },

        setOnboardingToken: (token: string) => {
          set(state => {
            if (!state.session || state.session.phase !== 'registration') return state;
            return {
              ...state,
              session: {
                ...state.session,
                registration: { ...state.session.registration, onboardingToken: token },
              },
            };
          });
        },

        // Convenience setters for onboarding
        setDocumentExtraction: (documentExtraction: DocumentExtractionResponse) => {
          set(state => {
            if (!state.session || state.session.phase !== 'onboarding') return state;
            return {
              ...state,
              session: {
                ...state.session,
                onboarding: { ...state.session.onboarding, documentExtraction },
              },
            };
          });
        },

        setSigningSessionId: (signingSessionId: string) => {
          set(state => {
            if (!state.session || state.session.phase !== 'onboarding') return state;
            return {
              ...state,
              session: {
                ...state.session,
                onboarding: { ...state.session.onboarding, signingSessionId },
              },
            };
          });
        },

        // Validation caching
        markValidated: (field: string, value: ValidatedField | Record<string, unknown>) => {
          set(state => {
            if (!state.session) return state;
            logger.info(`[UnifiedSession] Marked ${field} as validated`);
            return {
              ...state,
              session: {
                ...state.session,
                validatedData: {
                  ...state.session.validatedData,
                  [field]: value as ValidatedField | Record<string, unknown> | undefined,
                },
              },
            };
          });
        },

        isFieldValidated: (field: string, ageThresholdMs = VALIDATION_CACHE_DURATION) => {
          const session = get().session;
          if (!session) return false;

          const validated = session.validatedData[field];
          if (!validated) return false;

          if (
            'verifiedAt' in validated &&
            typeof (validated as unknown as { verifiedAt?: unknown }).verifiedAt === 'number'
          ) {
            const age = Date.now() - (validated as ValidatedField).verifiedAt;
            return age < ageThresholdMs;
          }

          if (
            'validatedAt' in validated &&
            typeof (validated as unknown as { validatedAt?: unknown }).validatedAt === 'number'
          ) {
            const age = Date.now() - (validated as unknown as { validatedAt: number }).validatedAt;
            return age < ageThresholdMs;
          }

          return true;
        },

        getValidatedField: (field: string) => {
          const session = get().session;
          if (!session) return undefined;

          const validated = session.validatedData[field];
          if (!validated) return undefined;

          if ('verifiedAt' in validated) {
            const verifiedAt = (validated as unknown as { verifiedAt?: unknown }).verifiedAt;
            if (
              typeof verifiedAt === 'number' &&
              Date.now() - verifiedAt > VALIDATION_CACHE_DURATION
            ) {
              return undefined;
            }
          }

          return validated;
        },

        // Recovery
        recover: (fromStep: SessionStep) => {
          set(state => {
            if (!state.session) return state;
            logger.info('[UnifiedSession] Session recovered:', {
              sessionId: state.session.sessionId,
              fromStep,
              phase: state.session.phase,
            });
            return {
              ...state,
              session: {
                ...state.session,
                isRecovered: true,
                recoveredFrom: fromStep,
                recoveryAttempt: (state.session.recoveryAttempt || 0) + 1,
              },
            };
          });
        },

        // Pending operations
        setPendingOperation: (field: string, status: 'pending' | 'failed', error?: string) => {
          set(state => {
            if (!state.session) return state;
            logger.info(`[UnifiedSession] Pending operation: ${field}=${status}`, { error });
            return {
              ...state,
              session: {
                ...state.session,
                pendingOperations: [
                  ...state.session.pendingOperations.filter(op => op.field !== field),
                  { field, status, error, timestamp: Date.now() },
                ],
              },
            };
          });
        },

        clearPendingOperation: (field: string) => {
          set(state => {
            if (!state.session) return state;
            logger.info(`[UnifiedSession] Cleared pending operation: ${field}`);
            return {
              ...state,
              session: {
                ...state.session,
                pendingOperations: state.session.pendingOperations.filter(op => op.field !== field),
              },
            };
          });
        },

        // Reset
        reset: () => {
          logger.info('[UnifiedSession] Full reset');
          set({ session: null, error: null, isLoading: false });
        },

        clearError: () => {
          set({ error: null });
        },
      }),
      {
        name: 'tankef-unified-session',
        version: 1,
        storage: createJSONStorage(() => sessionStorage),
        partialize: state => ({
          session: state.session,
          isLoading: state.isLoading,
          error: state.error?.message ? { message: state.error.message } : null,
        }),
      }
    )
  )
);

// G8: reset del store cuando TokenManager dispara logout. Sin esto, el
// onboarding de un nuevo usuario en la misma tab heredaba los datos del
// anterior (sessionId, personData, validatedData, etc.). El evento se
// emite desde TokenManager.clearTokens().
if (typeof window !== 'undefined') {
  window.addEventListener('tkf:session:logout', () => {
    useUnifiedSessionStore.getState().reset();
  });
}
